import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from '@themesberg/react-bootstrap';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import { rulesApi } from '../../services/api';
import { Routes } from '../../routes';

const defaultForm = {
  name: '',
  eventType: '',
  aggregate: 'COUNT',
  field: '',
  groupBy: 'subjects.email',
  window: 'PT1M',
  operator: '>=',
  threshold: '5',
  decision: 'BLOCK',
  action: '',
};

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await rulesApi.list();
      setRules(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name,
        eventType: form.eventType || null,
        condition: {
          aggregate: form.aggregate,
          field: form.aggregate === 'SUM' ? form.field : null,
          groupBy: form.groupBy,
          window: form.window,
          operator: form.operator,
          threshold: Number(form.threshold),
        },
        decision: form.decision,
        action: form.action || null,
      };
      await rulesApi.create(payload);
      setShowModal(false);
      setForm(defaultForm);
      setSuccess('Rule created');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleRule = async (rule) => {
    setError('');
    try {
      if (rule.enabled) await rulesApi.disable(rule.id);
      else await rulesApi.enable(rule.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    setError('');
    try {
      await rulesApi.delete(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const applyTemplate = (template) => {
    setForm({ ...defaultForm, ...template });
    setShowModal(true);
  };

  return (
    <>
      <PageHeader title="Velocity rules" subtitle="COUNT and SUM aggregation rules scoped by event type.">
        <Button variant="primary" onClick={() => setShowModal(true)}>New rule</Button>
      </PageHeader>

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6>Login attempts (COUNT)</h6>
              <p className="text-gray small mb-3">Block after 5 login attempts per email in 1 minute.</p>
              <Button size="sm" variant="outline-primary" onClick={() => applyTemplate({
                name: 'Login attempts per email',
                eventType: 'auth.login_attempt',
                aggregate: 'COUNT',
                groupBy: 'subjects.email',
                window: 'PT1M',
                operator: '>=',
                threshold: '5',
              })}>
                Use template
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6>Max withdrawal per day (SUM)</h6>
              <p className="text-gray small mb-3">Block when daily withdrawals for a customer exceed 50,000.</p>
              <Button size="sm" variant="outline-primary" onClick={() => applyTemplate({
                name: 'Max withdrawal per customer per day',
                eventType: 'payout.request',
                aggregate: 'SUM',
                field: 'value.amount',
                groupBy: 'subjects.customerId',
                window: 'P1D',
                operator: '>',
                threshold: '50000',
                action: 'REJECT_WITHDRAWAL',
              })}>
                Use template
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-3">Ghost users / shared identity</h5>
      <p className="text-gray small mb-3">
        For <code>member.registration</code> events: put identifiers in{' '}
        <code>subjects</code> (nationalId, phone, email, bankAccount, deviceId) and staff
        context in <code>attributes</code> (createdBy, branch). Each template counts
        registrations sharing that key.
      </p>
      <Row className="mb-4">
        {[
          {
            title: 'Same national ID → multiple members',
            blurb: 'REVIEW when the same nationalId appears on 2+ registrations in 30 days.',
            template: {
              name: 'Ghost: nationalId reused',
              eventType: 'member.registration',
              aggregate: 'COUNT',
              groupBy: 'subjects.nationalId',
              window: 'P30D',
              operator: '>=',
              threshold: '2',
              decision: 'REVIEW',
              action: 'FLAG_SHARED_NATIONAL_ID',
            },
          },
          {
            title: 'Same phone → multiple members',
            blurb: 'REVIEW when the same phone is used on 2+ registrations in 30 days.',
            template: {
              name: 'Ghost: phone reused',
              eventType: 'member.registration',
              aggregate: 'COUNT',
              groupBy: 'subjects.phone',
              window: 'P30D',
              operator: '>=',
              threshold: '2',
              decision: 'REVIEW',
              action: 'FLAG_SHARED_PHONE',
            },
          },
          {
            title: 'Same bank account → many members',
            blurb: 'BLOCK when one bankAccount is linked to 3+ registrations in 30 days.',
            template: {
              name: 'Ghost: bankAccount reused',
              eventType: 'member.registration',
              aggregate: 'COUNT',
              groupBy: 'subjects.bankAccount',
              window: 'P30D',
              operator: '>=',
              threshold: '3',
              decision: 'BLOCK',
              action: 'FLAG_SHARED_BANK_ACCOUNT',
            },
          },
          {
            title: 'Same device → many registrations',
            blurb: 'BLOCK when one deviceId creates 5+ members in 7 days.',
            template: {
              name: 'Ghost: device mass registration',
              eventType: 'member.registration',
              aggregate: 'COUNT',
              groupBy: 'subjects.deviceId',
              window: 'P7D',
              operator: '>=',
              threshold: '5',
              decision: 'BLOCK',
              action: 'FLAG_DEVICE_FARM',
            },
          },
          {
            title: 'Employee creates too many members',
            blurb: 'REVIEW when one createdBy staff ID registers 20+ members in a day.',
            template: {
              name: 'Ghost: employee registration spike',
              eventType: 'member.registration',
              aggregate: 'COUNT',
              groupBy: 'attributes.createdBy',
              window: 'P1D',
              operator: '>=',
              threshold: '20',
              decision: 'REVIEW',
              action: 'FLAG_EMPLOYEE_SPIKE',
            },
          },
        ].map((card) => (
          <Col md={6} lg={4} className="mb-3" key={card.template.name}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body>
                <h6>{card.title}</h6>
                <p className="text-gray small mb-3">{card.blurb}</p>
                <Button size="sm" variant="outline-primary" onClick={() => applyTemplate(card.template)}>
                  Use template
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <h5 className="mb-3">Card / payment gateway</h5>
      <p className="text-gray small mb-3">
        For <code>payment.authorize</code> / <code>payment.refund</code>: put{' '}
        <code>panHash</code>, <code>merchantId</code>, <code>ip</code>, <code>deviceId</code>,{' '}
        <code>email</code>, and <code>bin</code> in <code>subjects</code>; put amount in{' '}
        <code>value.amount</code>. Flowfence supports COUNT/SUM only — rules that need true
        distinct counts, geo/impossible-travel, decline-rate %, or baseline multipliers are
        not available yet; volume proxies below still catch common carding patterns.
      </p>
      <Row className="mb-4">
        {[
          {
            title: 'Card high-frequency authorizations',
            blurb: 'BLOCK when one panHash sees 5+ authorizations in 1 minute (rapid auth / testing).',
            template: {
              name: 'Card: high-frequency authorizations',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.panHash',
              window: 'PT1M',
              operator: '>=',
              threshold: '5',
              decision: 'BLOCK',
              action: 'EXCEEDED_VELOCITY_LIMIT',
            },
          },
          {
            title: 'Card cross-merchant burst',
            blurb: 'STEP-UP proxy: 3+ authorizations on one panHash in 10 minutes (often multi-MID probing).',
            template: {
              name: 'Card: cross-merchant burst',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.panHash',
              window: 'PT10M',
              operator: '>=',
              threshold: '3',
              decision: 'REVIEW',
              action: 'STEP_UP_3DS',
            },
          },
          {
            title: 'IP carding / spraying volume',
            blurb: 'BLOCK when one IP drives 10+ authorize attempts in 5 minutes (card spraying / bots).',
            template: {
              name: 'Network: IP carding volume',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.ip',
              window: 'PT5M',
              operator: '>=',
              threshold: '10',
              decision: 'BLOCK',
              action: 'BLOCK_IP_AND_DECLINE',
            },
          },
          {
            title: 'Device fingerprint card spraying',
            blurb: 'BLOCK when one deviceId submits 5+ authorizations in 15 minutes.',
            template: {
              name: 'Device: card spraying volume',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.deviceId',
              window: 'PT15M',
              operator: '>=',
              threshold: '5',
              decision: 'BLOCK',
              action: 'DECLINE',
            },
          },
          {
            title: 'Email multi-card abuse',
            blurb: 'REVIEW when one email is used on 4+ authorize attempts in 1 hour (multi-PAN probing).',
            template: {
              name: 'Customer: email multi-card volume',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.email',
              window: 'PT1H',
              operator: '>=',
              threshold: '4',
              decision: 'REVIEW',
              action: 'FLAG_FOR_REVIEW',
            },
          },
          {
            title: 'BIN enumeration spike',
            blurb: 'BLOCK when one BIN sees 100+ authorizations in 5 minutes (BIN testing).',
            template: {
              name: 'BIN: enumeration spike',
              eventType: 'payment.authorize',
              aggregate: 'COUNT',
              groupBy: 'subjects.bin',
              window: 'PT5M',
              operator: '>=',
              threshold: '100',
              decision: 'BLOCK',
              action: 'RATE_LIMIT_GATEWAY',
            },
          },
          {
            title: 'Merchant refund velocity',
            blurb: 'REVIEW when one merchantId posts 15+ refunds in 10 minutes.',
            template: {
              name: 'Merchant: refund velocity',
              eventType: 'payment.refund',
              aggregate: 'COUNT',
              groupBy: 'subjects.merchantId',
              window: 'PT10M',
              operator: '>=',
              threshold: '15',
              decision: 'REVIEW',
              action: 'FLAG_FOR_AUDIT',
            },
          },
          {
            title: 'Merchant hourly volume spike',
            blurb: 'REVIEW when one merchant processes over 1,000,000 in authorize amount within 1 hour (crude GPV spike proxy).',
            template: {
              name: 'Merchant: hourly volume spike',
              eventType: 'payment.authorize',
              aggregate: 'SUM',
              field: 'value.amount',
              groupBy: 'subjects.merchantId',
              window: 'PT1H',
              operator: '>',
              threshold: '1000000',
              decision: 'REVIEW',
              action: 'SUSPEND_PAYOUTS_AND_HOLD',
            },
          },
        ].map((card) => (
          <Col md={6} lg={4} className="mb-3" key={card.template.name}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body>
                <h6>{card.title}</h6>
                <p className="text-gray small mb-3">{card.blurb}</p>
                <Button size="sm" variant="outline-primary" onClick={() => applyTemplate(card.template)}>
                  Use template
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <h5 className="mb-3">Transaction backdating</h5>
      <p className="text-gray small mb-3">
        Velocity rules cannot compare calendar dates. Your app should detect{' '}
        <code>transactionDate &lt; today − 1 day</code> and emit{' '}
        <code>eventType: transaction.backdate</code> (include amount, createdBy, and the
        original dates in attributes). Templates below apply only to that event type.
      </p>
      <Row className="mb-4">
        {[
          {
            title: 'Any backdated transaction',
            blurb: 'REVIEW every backdated create (date older than yesterday).',
            template: {
              name: 'Backdate: any transaction',
              eventType: 'transaction.backdate',
              aggregate: 'COUNT',
              groupBy: 'subjects.transactionId',
              window: 'P1D',
              operator: '>=',
              threshold: '1',
              decision: 'REVIEW',
              action: 'FLAG_BACKDATED_TXN',
            },
          },
          {
            title: 'Large backdated transaction',
            blurb: 'BLOCK when a single backdated transaction amount exceeds 100,000.',
            template: {
              name: 'Backdate: amount over 100000',
              eventType: 'transaction.backdate',
              aggregate: 'SUM',
              field: 'value.amount',
              groupBy: 'subjects.transactionId',
              window: 'P1D',
              operator: '>',
              threshold: '100000',
              decision: 'BLOCK',
              action: 'REJECT_BACKDATED_LARGE_TXN',
            },
          },
          {
            title: 'Employee backdating spike',
            blurb: 'REVIEW when one createdBy posts 5+ backdated transactions in a day.',
            template: {
              name: 'Backdate: employee spike',
              eventType: 'transaction.backdate',
              aggregate: 'COUNT',
              groupBy: 'subjects.createdBy',
              window: 'P1D',
              operator: '>=',
              threshold: '5',
              decision: 'REVIEW',
              action: 'FLAG_EMPLOYEE_BACKDATE_SPIKE',
            },
          },
        ].map((card) => (
          <Col md={6} lg={4} className="mb-3" key={card.template.name}>
            <Card border="light" className="shadow-sm h-100">
              <Card.Body>
                <h6>{card.title}</h6>
                <p className="text-gray small mb-3">{card.blurb}</p>
                <Button size="sm" variant="outline-primary" onClick={() => applyTemplate(card.template)}>
                  Use template
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <p className="p-4 mb-0">Loading rules…</p>
          ) : rules.length === 0 ? (
            <p className="p-4 mb-0 text-gray">No rules yet. Create one to start evaluating decisions.</p>
          ) : (
            <Table responsive hover className="align-items-center mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Name</th>
                  <th>Event</th>
                  <th>Aggregate</th>
                  <th>Group by</th>
                  <th>Window</th>
                  <th>Decision</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <Link to={`${Routes.Rules.path}/${rule.id}`} className="fw-bold">
                        {rule.name}
                      </Link>
                    </td>
                    <td><code>{rule.eventType || '*'}</code></td>
                    <td>{rule.condition.aggregate}</td>
                    <td><code>{rule.condition.groupBy}</code></td>
                    <td>{rule.condition.window}</td>
                    <td>{rule.decision}</td>
                    <td>
                      <Badge bg={rule.enabled ? 'success' : 'secondary'}>
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        as={Link}
                        to={`${Routes.Rules.path}/${rule.id}`}
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                      >
                        View
                      </Button>
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => toggleRule(rule)}>
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removeRule(rule.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create rule</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control required value={form.name} onChange={(e) => update('name', e.target.value)} />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Event type</Form.Label>
                <Form.Control value={form.eventType} onChange={(e) => update('eventType', e.target.value)} placeholder="auth.login_attempt" />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Aggregate</Form.Label>
                <Form.Select value={form.aggregate} onChange={(e) => update('aggregate', e.target.value)}>
                  <option value="COUNT">COUNT</option>
                  <option value="SUM">SUM</option>
                </Form.Select>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Group by</Form.Label>
                <Form.Control required value={form.groupBy} onChange={(e) => update('groupBy', e.target.value)} />
              </Col>
              {form.aggregate === 'SUM' ? (
                <Col md={4} className="mb-3">
                  <Form.Label>Field</Form.Label>
                  <Form.Control required value={form.field} onChange={(e) => update('field', e.target.value)} placeholder="value.amount" />
                </Col>
              ) : null}
              <Col md={3} className="mb-3">
                <Form.Label>Window</Form.Label>
                <Form.Control required value={form.window} onChange={(e) => update('window', e.target.value)} placeholder="PT1M" />
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label>Operator</Form.Label>
                <Form.Select value={form.operator} onChange={(e) => update('operator', e.target.value)}>
                  <option value=">">&gt;</option>
                  <option value=">=">&gt;=</option>
                  <option value="<">&lt;</option>
                  <option value="<=">&lt;=</option>
                  <option value="==">==</option>
                  <option value="!=">!=</option>
                </Form.Select>
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label>Threshold</Form.Label>
                <Form.Control required type="number" min="0" value={form.threshold} onChange={(e) => update('threshold', e.target.value)} />
              </Col>
              <Col md={3} className="mb-3">
                <Form.Label>Decision</Form.Label>
                <Form.Select value={form.decision} onChange={(e) => update('decision', e.target.value)}>
                  <option value="ALLOW">ALLOW</option>
                  <option value="REVIEW">REVIEW</option>
                  <option value="BLOCK">BLOCK</option>
                </Form.Select>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Action</Form.Label>
                <Form.Control value={form.action} onChange={(e) => update('action', e.target.value)} placeholder="REQUIRE_COOLDOWN" />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Create</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
